import { useMemo, useRef, useState } from 'react';
import { Search, Undo2, X } from 'lucide-react';
import { CATALOG, type CatalogForm } from '@generated/lib/formsCatalog';
import { FormCard } from '@generated/components/catalog/FormCard';
import { CancelRequestDialog } from '@generated/components/form/CancelRequestDialog';

export function FormsCatalog() {
  const [term, setTerm] = useState('');
  const [cancelOpen, setCancelOpen] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);

  const results = useMemo(() => {
    const q = term.trim().toLowerCase();
    if (!q) return CATALOG;
    return CATALOG.filter(
      (f) =>
        f.title.toLowerCase().includes(q) ||
        f.summary.toLowerCase().includes(q) ||
        f.category.toLowerCase().includes(q) ||
        f.keywords.some((t: string) => t.toLowerCase().includes(q)),
    );
  }, [term]);

  const grouped = useMemo(() => {
    const groups: { category: string; forms: CatalogForm[] }[] = [];
    for (const form of results) {
      let g = groups.find((item) => item.category === form.category);
      if (!g) {
        g = { category: form.category, forms: [] };
        groups.push(g);
      }
      g.forms.push(form);
    }
    return groups;
  }, [results]);

  return (
    <div className="mx-auto w-full max-w-[1180px] px-4 py-8 sm:px-6 sm:py-14">
      <header className="border-b border-[hsl(var(--rule-soft))] pb-10">
        <p className="text-[12px] font-semibold tracking-[0.16em] text-muted-foreground">
          מדור תלמידים · הפקולטה להנדסה · תשפ״ו
        </p>
        <h1 className="mt-4 max-w-[20ch] text-4xl font-normal leading-[1.05] tracking-[-0.015em] text-foreground sm:text-6xl">
          טפסים לסטודנטים בפקולטה להנדסה
        </h1>
        <p className="mt-5 max-w-[58ch] text-sm leading-relaxed text-muted-foreground sm:text-base">
          כל הטפסים המנהליים במקום אחד. בוחרים טופס, ממלאים אותו במלואו, עוברים על הסיכום ומורידים עותק חתום להדפסה.
        </p>

        <div className="mt-8 flex max-w-[560px] items-center gap-3 rounded-lg border border-[hsl(var(--rule))] bg-[hsl(var(--field-bg))] px-4">
          <Search className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          <label htmlFor="catalog-search" className="sr-only">
            חיפוש טופס
          </label>
          <input
            id="catalog-search"
            ref={searchRef}
            value={term}
            onChange={(event) => setTerm(event.target.value)}
            placeholder="חיפוש טופס לפי שם או נושא"
            className="h-12 w-full min-w-0 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
          />
          {term ? (
            <button
              type="button"
              onClick={() => setTerm('')}
              aria-label="ניקוי החיפוש"
              className="text-muted-foreground transition-colors duration-150 hover:text-foreground"
            >
              <X className="size-4" aria-hidden="true" />
            </button>
          ) : (
            <kbd className="hidden shrink-0 border border-[hsl(var(--rule-soft))] px-1.5 py-0.5 text-[10px] text-muted-foreground sm:block">
              /
            </kbd>
          )}
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          {term ? `${results.length} מתוך ${CATALOG.length} טפסים תואמים לחיפוש` : `${CATALOG.length} טפסים בקטלוג`}
        </p>
      </header>

      <CancelRequestDialog open={cancelOpen} onOpenChange={setCancelOpen} onCancelled={() => undefined} />

      {grouped.length === 0 ? (
        <div className="mt-12 rounded-lg border border-dashed border-[hsl(var(--rule))] bg-[hsl(var(--surface-2))] px-6 py-16 text-center">
          <p className="text-lg font-medium text-foreground">לא נמצא טופס תואם</p>
          <p className="mx-auto mt-2 max-w-[48ch] text-sm text-muted-foreground">
            אפשר לנסות מילת חיפוש אחרת, למשל ״מילואים״, ״בחינה״ או ״הגשה״.
          </p>
          <button
            type="button"
            onClick={() => setTerm('')}
            className="mt-6 inline-flex h-11 items-center rounded-md border border-[hsl(var(--rule))] bg-[hsl(var(--surface-1))] px-5 text-[13px] font-medium text-foreground transition-colors duration-150 hover:bg-[hsl(var(--surface-3))]"
          >
            ניקוי החיפוש
          </button>
        </div>
      ) : (
        grouped.map((group) => (
          <section key={group.category} className="mt-12" aria-labelledby={`cat-${group.category}`}>
            <div className="mb-5 flex items-baseline justify-between gap-4 border-b border-[hsl(var(--rule-soft))] pb-2">
              <h2 id={`cat-${group.category}`} className="text-lg font-medium text-foreground">
                {group.category}
              </h2>
              <span className="text-[11px] tracking-[0.12em] text-muted-foreground">{group.forms.length} טפ��ים</span>
            </div>
            <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
              {group.forms.map((form) => (
                <FormCard
                  key={form.id}
                  form={form}
                  index={CATALOG.findIndex((item) => item.id === form.id)}
                  footerAction={
                    form.id === 'post-bidding' && form.status === 'available' ? (
                      <button
                        type="button"
                        onClick={() => setCancelOpen(true)}
                        className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-md border border-[hsl(var(--rule))] bg-[hsl(var(--surface-1))] px-4 text-[13px] font-medium text-foreground transition-colors duration-150 hover:bg-[hsl(var(--surface-3))]"
                      >
                        <Undo2 className="size-4 text-muted-foreground" aria-hidden="true" />
                        ביטול בקשה קיימת
                      </button>
                    ) : undefined
                  }
                />
              ))}
            </div>
          </section>
        ))
      )}

      <section
        aria-labelledby="guidance-title"
        className="mt-14 grid gap-px overflow-hidden rounded-lg border border-[hsl(var(--rule))] bg-[hsl(var(--rule-soft))] md:grid-cols-4"
      >
        <div className="bg-[hsl(var(--tint-sky))] p-6">
          <h2 id="guidance-title" className="text-lg font-medium text-foreground">
            לפני שממלאים טופס
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            כדאי לוודא שהפרטים המזהים מעודכנים ושכל האישורים הנדרשים נמצאים בהישג יד.
          </p>
        </div>
        <div className="bg-[hsl(var(--surface-1))] p-6">
          <h3 className="text-sm font-medium text-foreground">טיוטה נשמרת אוטומטית</h3>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            טופס שהתחלתם למלא נשמר בדפדפן זה בלבד, כך שאפשר לחזור אליו גם אחרי רענון הדף.
          </p>
        </div>
        <div className="bg-[hsl(var(--surface-1))] p-6">
          <h3 className="text-sm font-medium text-foreground">עותק חתום להדפסה</h3>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            בסיום המילוי מתקבל טופס מסודר עם חתימה, שניתן להדפיס או להוריד כקובץ PDF ולשמור במחשב.
          </p>
        </div>
        <div className="bg-[hsl(var(--surface-1))] p-6">
          <h3 className="text-sm font-medium text-foreground">מעקב ובירורים</h3>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            לאחר הגשת הטופס הטיפול מתבצע על ידי מדור התלמידים והודעת עדכון תישלח לכתובת הדוא״ל.
          </p>
        </div>
      </section>
    </div>
  );
}
